/**
 * @file CategoryTreePage.tsx
 * @description Категории товаров — дерево с перетаскиванием (phase14.0.2;
 * phase19.22 W22 — вид товара по умолчанию; phase19.31 — образец (б)
 * разбора B, SC52).
 *
 * Заголовок «Категории» в верхней панели, главное действие «+ Категория».
 * В строке — название, вид товара, число товаров и «На сайте» словами; всё,
 * что меняет категорию (подкатегория, правка, публикация, маркировка
 * «Честный знак», удаление с подтверждением), — в «⋯» строки.
 *
 * Данные и действия — `useCategoryTree`, дерево — `CategoryTreeList`.
 */

import { FolderTree, Plus } from "lucide-react";
import { useState } from "react";

import { CategoryEditDialog } from "../../../components/catalog/CategoryEditDialog";
import { CategoryMarkingDialog } from "../../../components/catalog/CategoryMarkingDialog";
import { categoryRowActions } from "../../../components/catalog/categories/category-row-meta";
import { CategoryTreeList } from "../../../components/catalog/categories/CategoryTreeList";
import { useCategoryTree } from "../../../components/catalog/categories/useCategoryTree";
import { useProductKinds } from "../../../components/catalog/product-kinds/useProductKinds";
import { Button } from "../../../components/ui/button";
import { CabinetPageHeader } from "../../../components/ui/CabinetPageHeader";
import { ListEmpty, ListError, ListSkeleton } from "../../../components/ui/ListStates";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import type { CatalogCategory } from "../../../lib/api-catalog";
import { resolveCategoryKind } from "../../../lib/product-kind-resolve";

export function CategoryTreePage() {
  const page = useCategoryTree();
  const { kinds, kindsById } = useProductKinds();
  const [editTarget, setEditTarget] = useState<{ category?: CatalogCategory; parentId?: string } | null>(null);
  const [markingTarget, setMarkingTarget] = useState<CatalogCategory | null>(null);

  const rowActions = (c: CatalogCategory) =>
    categoryRowActions(c, {
      onAddChild: (parent) => setEditTarget({ parentId: parent.id }),
      onEdit: (category) => setEditTarget({ category }),
      onMarking: setMarkingTarget,
      onPublish: page.setPublish,
      onDelete: (category) => void page.remove(category),
    });

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Категории"
        subtitle="Разделы каталога и их показ на сайте"
        action={
          <Button variant="brand" onClick={() => setEditTarget({})}>
            <Plus className="size-4" />
            Категория
          </Button>
        }
      />

      {page.error && page.categories.length === 0 ? (
        <ListError message={page.error} onRetry={() => void page.refresh()} />
      ) : page.loading && page.categories.length === 0 ? (
        <ListSkeleton rows={8} compact={false} />
      ) : page.tree.length === 0 ? (
        <ListEmpty spec={{ icon: FolderTree, title: "Категорий пока нет", description: "Создайте первую — товары раскладываются по категориям на сайте" }} filtered={false} />
      ) : (
        <CategoryTreeList
          tree={page.tree}
          expanded={page.expanded}
          onToggle={page.toggle}
          byId={page.byId}
          kindsById={kindsById}
          rowActions={rowActions}
          onReorder={page.reorder}
        />
      )}

      {editTarget !== null ? (
        <CategoryEditDialog
          open
          category={editTarget.category ?? null}
          parentId={editTarget.parentId ?? null}
          kinds={kinds}
          inheritedKindName={
            editTarget.category
              ? (editTarget.category.inheritedKind?.name ?? null)
              : (resolveCategoryKind(editTarget.parentId, page.byId, kindsById).kind?.name ?? null)
          }
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void page.refresh();
          }}
          onImageChanged={() => void page.refresh()}
        />
      ) : null}

      {markingTarget ? (
        <CategoryMarkingDialog
          open
          category={markingTarget}
          onClose={() => setMarkingTarget(null)}
          onSaved={() => {
            setMarkingTarget(null);
            void page.refresh();
          }}
        />
      ) : null}

      <ConfirmDialog request={page.confirmRequest} onSettle={page.settleConfirm} />
    </div>
  );
}
